// Call Sheet's guess control: the input, Submit beside it, and the dropdown.
//
// **A typed guess is always a selected title record, never raw typing**
// (CLAUDE.md). Submit stays disabled while there is text in the box that does
// not resolve to a title — picked from the dropdown, or typed as an exact match
// under the forgiving comparison.
//
// **An empty box is not a dead end: the button reads Skip.** A player who
// cannot name it should be able to spend a guess to see the next face rather
// than typing something they know is wrong, and the button is never a dead
// control — it is disabled only when there is half a title in the box.
//
// **Submit sits under the search box at the full width of the column**, which is
// the control Running Order has.
//
// **The suggestion list floats over whatever is beneath it and never pushes
// anything down.** It covers Submit and the counter while it is open, and they
// are back the moment it closes — and the button is hidden rather than left
// peeping out below the list, which read as a second control. So the guess area
// is the same height whatever is being typed, which is what lets the page hold
// still.
//
// **The list is fitted to the room the keyboard leaves**, in whole rows: three
// where there is space, fewer where there is not, never a part row, and the rest
// reached by scrolling it. That is the other half of holding the page still —
// the page is asked to make room for two rows and no more, and whatever room
// that turns out to leave is what the list is sized to.
//
// **The list opens on editing and on nothing else.** It used to open on focus
// too, which meant picking a title closed it and then reopened it the moment
// focus came back to the field — a list offering the title that is already in
// the box, on top of the button needed to submit it.
//
// **Autocorrect, autocapitalisation and the spell checker are all off.** A title
// needs exact spelling and there is no fuzzy matching, so a phone quietly
// rewriting what was typed can only turn a correct guess into a wrong one.
import { useEffect, useMemo, useRef, useState } from 'react';
import { keepAboveKeyboard, MARGIN } from '../../lib/keyboard.ts';
import { isSideways } from '../../lib/sideways.ts';
import { resolve, search, kindLabel, type TitleRecord } from './titles.ts';

/** The most rows the list ever shows, keyboard or no keyboard. */
const MAX_ROWS = 3;
/** The list's own borders. */
const BORDERS = 2;
/** What a row has to clear to count as fitting: the list's borders and the same
 *  clearance above the keyboard the scroll rule uses. Taken from there rather
 *  than copied, because the two deciding different things by a pixel is how a
 *  row that fits gets counted as one that does not. */
const CHROME = BORDERS + MARGIN;

/** A length token off an element, in pixels. Registered properties resolve to a
 *  number; an unregistered one would come back as the text it was written as. */
function num(el: Element, token: string): number {
  const value = parseFloat(getComputedStyle(el).getPropertyValue(token));
  return Number.isFinite(value) ? value : 0;
}

interface Props {
  list: TitleRecord[];
  disabled: boolean;
  /** Everything that has to stay above the keyboard: this control and the
   *  guesses counter under it. The game screen owns it, because the counter is
   *  not this component's to know about. */
  region: React.RefObject<HTMLElement | null>;
  onGuess: (title: TitleRecord) => void;
  onSkip: () => void;
}

export function TitleInput({ list, disabled, region, onGuess, onSkip }: Props) {
  const [text, setText] = useState('');
  // **The record picked from the dropdown, kept as a record** (owner, 12
  // October). Keeping only its name and looking the name up again at Submit is
  // what marked correct answers wrong: "Hannibal (Show)" was picked, "Hannibal"
  // was looked up, and the film came back first. Typing anything clears it.
  const [chosen, setChosen] = useState<TitleRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const entry = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(() => search(text, list), [text, list]);
  const typed = useMemo(() => resolve(text, list), [text, list]);
  const picked = chosen ?? typed;
  const showList = open && text.trim() !== '' && suggestions.length > 0;
  // Only once there is something to say about: an empty box is not "no match".
  const showNoMatch = open && text.trim() !== '' && suggestions.length === 0;

  useEffect(() => { setActive(0); }, [text]);

  // The guess area is kept above the keyboard while the field is focused
  // (src/lib/keyboard.ts): the smallest scroll that clears it, worked out once
  // as the keyboard opens and then left alone.
  //
  // **The page is asked for two rows, and the list is fitted to what that
  // leaves.** Those are the two halves of keeping it still. Asking for the full
  // three was what made it jump 110px on a real phone: it was reserving room for
  // rows that could perfectly well be scrolled to. Asking for two means it
  // usually need not move at all, and then however many whole rows fit — three,
  // two, or at worst one — is what the list shows.
  //
  // The stylesheet owns both figures, next to the rules they have to agree with,
  // and this reads them rather than keeping a second copy.
  useEffect(() => {
    const el = field.current;
    if (!el) return;

    /** Where the list's top edge sits, in viewport coordinates. */
    const listTop = () =>
      el.getBoundingClientRect().bottom + num(el, '--cs-list-gap');

    return keepAboveKeyboard(el, {
      // **The search box, and one row below it.** Not the whole guess area: the
      // list floats over Submit and the counter anyway, so demanding they stay
      // visible was demanding room for things that are covered the moment the
      // list opens — and the counter's bottom edge was what made the page shift
      // 7px on an iPhone. On a screen too small for even this, the page gives
      // and the counter goes under the keyboard; that is the trade, and it is
      // the right way round, because a page that holds still is worth more than
      // a line of secondary text.
      region: () => el,
      reserve: () => num(el, '--cs-list-min'),
      // **Sideways, the search box goes to the top of what the keyboard
      // leaves**, 4px down, so the faces and names scroll away above it and
      // every pixel under the box is the list's. Anchoring on the names instead
      // left room for one suggestion row on an iPhone 15 Pro Max; two need the
      // names' 40px. One fixed place, so typing the first letter, the list
      // opening, or the keyboard growing a predictive row cannot move it again.
      // Upright the smallest scroll is still the rule.
      anchor: () => {
        if (!isSideways()) return null;
        return el.getBoundingClientRect().top + window.scrollY - 4;
      },
      // Called once the page has stopped moving, so this measures where things
      // have actually ended up rather than where they were.
      onSettle: (keyboardOpen) => {
        const control = box.current;
        if (!control) return;
        if (!keyboardOpen) {
          // No keyboard: the full three, as on a desktop.
          control.style.removeProperty('--cs-rows');
          return;
        }
        const view = window.visualViewport;
        const row = num(el, '--cs-row');
        if (!view || !row) return;
        // Whole rows only. CHROME is the list's own borders plus the breathing
        // room it keeps off the keyboard, the same 8px the scroll rule uses.
        const room = view.offsetTop + view.height - listTop() - CHROME;
        const rows = Math.min(MAX_ROWS, Math.max(1, Math.floor(room / row)));
        control.style.setProperty('--cs-rows', String(rows));
      }
    });
  }, [region]);

  // A click outside closes the list. The dropdown overlays the page on desktop,
  // so it has to give way to whatever is clicked next.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  function choose(title: TitleRecord) {
    setText(title.title);
    setChosen(title);
    setOpen(false);
  }

  const empty = text.trim() === '';

  function submit() {
    if (disabled) return;
    if (empty) onSkip();
    else if (picked) onGuess(picked);
    else return;                 // half a title typed: nothing to submit yet
    setText('');
    setChosen(null);
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!showList) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    // Enter takes the highlighted suggestion where the list is open, and
    // otherwise submits what is already resolved. Enter is how this game is
    // played on a keyboard, so it should never do nothing.
    if (showList) choose(suggestions[active]!);
    else submit();
  }

  return (
    <div className={`cs-control${showList || showNoMatch ? ' cs-listing' : ''}`} ref={box}>
      <div className="cs-entry" ref={entry}>
        <input
          ref={field}
          className="input cs-input"
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setChosen(null); setOpen(true); }}
          onKeyDown={onKey}
          placeholder="Type a movie or show title"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          role="combobox"
          aria-expanded={showList}
          aria-autocomplete="list"
          aria-label="Your guess"
          disabled={disabled}
        />
        {/* Skip on an empty box, Submit otherwise — and disabled only for text
            that does not resolve, which is the one state with nothing to do. */}
        <button type="button" className="btn btn-primary cs-submit"
                disabled={disabled || (!empty && !picked)} onClick={submit}>
          {empty ? 'Skip' : 'Submit'}
        </button>
      </div>

      {showList && (
        <ul className="cs-suggestions" role="listbox">
          {suggestions.map((t, i) => {
          return (
            <li key={`${t.kind}-${t.id}`}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                className={`cs-suggestion${i === active ? ' is-active' : ''}`}
                // Down rather than click: a click fires after blur, by which
                // time the list has closed and taken the row with it.
                onPointerDown={(e) => { e.preventDefault(); choose(t); }}
                onMouseEnter={() => setActive(i)}
              >
                <span className="cs-suggestion-title">{t.title}</span>
                <span className="cs-suggestion-meta">
                  {t.year ?? ''}
                  <span className="cs-suggestion-kind">{kindLabel(t.kind)}</span>
                </span>
              </button>
            </li>
          );
          })}
        </ul>
      )}

      {showNoMatch && (
        <p className="cs-no-match">No title matches — spelling must be exact.</p>
      )}
    </div>
  );
}
